import React, { useState, useEffect } from 'react';
import { Avatar, Button, Toast, DotLoading } from 'antd-mobile';
import {
    RightOutline
} from 'antd-mobile-icons';
import liff from '@line/liff';
import { useStore } from '../store/useStore';
import { getSmokingUnit } from '@puff/shared';
import { theme } from '../theme';
import {
    Cloud, Clock, BarChart3, Target, Wallet, Rocket,
    CircleDollarSign, Wind, Cigarette,
    Leaf, Clock4, Clock8, Clock12, TrendingDown, Ban
} from 'lucide-react';

interface InviteItem {
    code: string;
    label: string;
    createdAt: string;
    viewerName?: string;
    viewerPicture?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://porddee-api.lungnuek.workers.dev/api';

// ── Shared Helper Components (Copied from SettingsPanel for self-containment) ──

const PopupModal: React.FC<{
    visible: boolean; onClose: () => void; title: string; subtitle?: string; icon?: React.ReactNode; children: React.ReactNode;
}> = ({ visible, onClose, title, subtitle, icon, children }) => {
    if (!visible) return null;
    return (
        <>
            <div onClick={onClose} style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.5)', zIndex: 2000,
                animation: 'fadeIn 0.2s ease-out', backdropFilter: 'blur(2px)',
            }} />
            <div style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                zIndex: 2001, width: '90%', maxWidth: 360,
                animation: 'popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            }}>
                <div style={{
                    background: 'white', borderRadius: 24,
                    padding: '28px 20px 20px', textAlign: 'center',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                }}>
                    {icon && (
                        <div style={{
                            width: 64, height: 64, borderRadius: '50%',
                            background: '#f8fafc', color: theme.primary,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px',
                        }}>
                            {icon}
                        </div>
                    )}
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{title}</h3>
                    {subtitle && <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 24px' }}>{subtitle}</p>}

                    <div style={{ textAlign: 'left' }}>
                        {children}
                    </div>
                </div>
            </div>
        </>
    );
};

const ModalActions: React.FC<{
    onCancel: () => void;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: string;
}> = ({ onCancel, onConfirm, confirmText = 'บันทึก', cancelText = 'ยกเลิก', confirmColor = theme.primary }) => (
    <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <button onClick={onCancel} style={{
            flex: 1, padding: '12px', borderRadius: 14, border: 'none',
            background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 15, cursor: 'pointer',
        }}>{cancelText}</button>
        <button onClick={onConfirm} style={{
            flex: 1, padding: '12px', borderRadius: 14, border: 'none',
            background: confirmColor, color: 'white', fontWeight: 600, fontSize: 15, cursor: 'pointer',
            boxShadow: `0 8px 16px ${confirmColor}30`
        }}>{confirmText}</button>
    </div>
);

const OptionCard: React.FC<{
    icon: React.ReactNode; label: string; sublabel?: string;
    selected: boolean; onClick: () => void; color?: string;
}> = ({ icon, label, sublabel, selected, onClick, color }) => (
    <button onClick={onClick} style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '16px 8px', borderRadius: 16,
        border: selected ? `2px solid ${color || theme.primary}` : '2px solid #e2e8f0',
        background: selected ? `${color || theme.primary}08` : 'white',
        cursor: 'pointer', textAlign: 'center',
        transition: 'all 0.2s ease', gap: 10,
        width: '100%', height: '100%',
    }}>
        <div style={{
            color: selected ? (color || theme.primary) : '#64748b',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            {React.isValidElement(icon) ? React.cloneElement(icon as any, { size: 32, strokeWidth: selected ? 2 : 1.5 }) : icon}
        </div>
        <div>
            <div style={{ fontWeight: selected ? 700 : 600, fontSize: 13, color: selected ? (color || theme.primary) : '#64748b' }}>{label}</div>
            {sublabel && <div style={{ fontSize: 11, color: '#94a3b8', margin: '4px auto 0' }}>{sublabel}</div>}
        </div>
    </button>
);

const NumberInputSheet: React.FC<{
    value: number; onChange: (v: number) => void; onConfirm: () => void; onCancel: () => void;
    unit: string; min?: number; max?: number; presets?: number[]; color?: string;
}> = ({ value, onChange, onConfirm, onCancel, unit, min = 1, max = 100, presets, color = theme.primary }) => (
    <div>
        <div style={{ background: '#f8fafc', borderRadius: 20, padding: '24px', textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 44, fontWeight: 900, color, marginBottom: 4 }}>
                {value}<span style={{ fontSize: 16, fontWeight: 600, color: '#94a3b8', marginLeft: 8 }}>{unit}</span>
            </div>
            <input type="range" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))} style={{ width: '100%', accentColor: color }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                <span>{min}</span><span>{Math.round(max / 2)}</span><span>{max}</span>
            </div>
        </div>
        {presets && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 20 }}>
                {presets.map(v => (
                    <button key={v} onClick={() => onChange(v)} style={{
                        padding: '8px 18px', borderRadius: 12, fontSize: 14, fontWeight: 700,
                        border: value === v ? `2px solid ${color}` : '2px solid #e2e8f0',
                        background: value === v ? `${color}15` : 'white',
                        color: value === v ? color : '#64748b',
                        cursor: 'pointer', transition: 'all 0.2s ease',
                    }}>{v} {unit}</button>
                ))}
            </div>
        )}
        <ModalActions onCancel={onCancel} onConfirm={onConfirm} confirmColor={color} />
    </div>
);

const CostInputSheet: React.FC<{
    value: number; onChange: (v: number) => void; onConfirm: () => void; onCancel: () => void; presets: number[];
}> = ({ value, onChange, onConfirm, onCancel, presets }) => (
    <div>
        <div style={{ background: '#f8fafc', borderRadius: 20, padding: '24px', textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 36, fontWeight: 900, color: '#f59e0b', marginBottom: 8 }}>฿{value.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>บาท</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 16 }}>
            {presets.map(v => (
                <button key={v} onClick={() => onChange(v)} style={{
                    padding: '8px 14px', borderRadius: 12, fontSize: 14, fontWeight: 700,
                    border: value === v ? '2px solid #f59e0b' : '2px solid #e2e8f0',
                    background: value === v ? '#fef3c7' : 'white',
                    color: value === v ? '#b45309' : '#64748b', cursor: 'pointer',
                }}>฿{v.toLocaleString()}</button>
            ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
            <span style={{ fontSize: 14, color: '#94a3b8' }}>พิมพ์เอง:</span>
            <input type="number" value={value} onChange={e => onChange(Number(e.target.value) || 0)} style={{
                width: 120, padding: '10px 14px', borderRadius: 12, border: '2px solid #e2e8f0',
                fontSize: 16, fontWeight: 700, textAlign: 'center', outline: 'none', color: '#0f172a',
            }} />
        </div>
        <ModalActions onCancel={onCancel} onConfirm={onConfirm} confirmColor="#f59e0b" />
    </div>
);

// ── Menu Item Row ──
const MenuItem: React.FC<{
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    rightContent?: React.ReactNode;
}> = ({ icon, label, onClick, rightContent }) => (
    <div onClick={onClick} style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px',
        cursor: onClick ? 'pointer' : 'default', background: 'white', border: 'none',
    }}>
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#1e293b', flexShrink: 0,
        }}>
            {icon}
        </div>
        <div style={{ flex: 1, fontSize: 16, fontWeight: 500, color: '#0f172a' }}>
            {label}
        </div>
        <div style={{ fontSize: 14, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 400 }}>
            {rightContent}
        </div>
    </div>
);


export const SideMenu: React.FC<{
    onClose: () => void;
    setActiveTab: (tab: any) => void;
}> = ({ onClose, setActiveTab }) => {
    const { user, updateUserSettings } = useStore();
    const unit = getSmokingUnit(user?.smokingType);

    // Invite states
    const [invites, setInvites] = useState<InviteItem[]>([]);
    const [loadingInvites, setLoadingInvites] = useState(false);
    const [loadingInviteBtn, setLoadingInviteBtn] = useState(false);

    useEffect(() => {
        if (user?.id) fetchInvites();
    }, [user?.id]);

    const fetchInvites = async () => {
        if (!user?.id) return;
        setLoadingInvites(true);
        try {
            const res = await fetch(`${API_BASE_URL}/invites/${user.id}`);
            if (res.ok) {
                const data = await res.json();
                setInvites(data.invites || []);
            }
        } catch (err) {
            console.error('Fetch invites error:', err);
        } finally {
            setLoadingInvites(false);
        }
    };

    const handleDeleteInvite = async (code: string) => {
        const confirmed = window.confirm('ต้องการลบคำเชิญนี้ใช่หรือไม่?');
        if (!confirmed) return;
        try {
            const res = await fetch(`${API_BASE_URL}/invite/${code}`, { method: 'DELETE' });
            if (res.ok) {
                setInvites(prev => prev.filter(inv => inv.code !== code));
                Toast.show({ content: 'ลบคำเชิญแล้ว', icon: 'success' });
            } else {
                Toast.show({ content: 'ลบไม่สำเร็จ', icon: 'fail' });
            }
        } catch {
            Toast.show({ content: 'ลบไม่สำเร็จ', icon: 'fail' });
        }
    };

    const handleInvite = async () => {
        if (!user?.id || loadingInviteBtn) return;
        setLoadingInviteBtn(true);
        try {
            const res = await fetch(`${API_BASE_URL}/invite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.id, label: 'ครอบครัว' }),
            });
            if (!res.ok) throw new Error('Failed to create invite');
            const { code } = await res.json();

            const liffUrl = `https://liff.line.me/2009156565-sN0haQ3J?invite=${code}`;
            const appShareUrl = `https://app.porddee.com/?invite=${code}`;
            const name = user.displayName || 'เพื่อนของคุณ';
            let days = 0;
            if (user.quitDate) {
                days = Math.floor((Date.now() - new Date(user.quitDate).getTime()) / (1000 * 60 * 60 * 24));
            }
            Toast.clear();

            try {
                const flexMsg = {
                    type: 'flex',
                    altText: `คำเชิญจาก ${name} ให้มาเป็นกำลังใจ ❤️`,
                    contents: {
                        type: 'bubble',
                        size: 'mega',
                        body: {
                            type: 'box',
                            layout: 'vertical',
                            paddingAll: 'xl',
                            contents: [
                                {
                                    type: 'box',
                                    layout: 'horizontal',
                                    alignItems: 'center',
                                    contents: [
                                        {
                                            type: 'box',
                                            layout: 'vertical',
                                            backgroundColor: '#ffe4e6',
                                            width: '72px',
                                            height: '72px',
                                            cornerRadius: '100px',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            contents: [
                                                { type: 'text', text: '❤️', size: '3xl', align: 'center', margin: 'none' }
                                            ],
                                            flex: 0
                                        },
                                        {
                                            type: 'box',
                                            layout: 'vertical',
                                            margin: 'lg',
                                            justifyContent: 'center',
                                            contents: [
                                                { type: 'text', text: 'คำเชิญครอบครัว', weight: 'bold', size: 'md', color: '#1e293b' },
                                                { type: 'text', text: 'ผู้ร่วมทางสู่ปอดดี', weight: 'bold', size: 'xl', color: '#e11d48', wrap: true }
                                            ]
                                        }
                                    ]
                                },
                                {
                                    type: 'text',
                                    text: 'เส้นทางการเลิกบุหรี่ไม่ได้ง่ายเสมอไป "พลังจากครอบครัว" คือสิ่งสำคัญที่สุดที่จะช่วยให้ไปถึงเป้าหมายได้สำเร็จ มาเป็นกำลังใจให้กันนะ ✨',
                                    size: 'md',
                                    color: '#64748b',
                                    margin: 'lg',
                                    wrap: true
                                },
                                { type: 'separator', margin: 'xl' },
                                {
                                    type: 'box',
                                    layout: 'vertical',
                                    margin: 'lg',
                                    spacing: 'md',
                                    contents: [
                                        {
                                            type: 'box',
                                            layout: 'horizontal',
                                            alignItems: 'center',
                                            contents: [
                                                { type: 'text', text: '👤', size: 'md', flex: 0 },
                                                { type: 'text', text: ' ผู้ท้าชิง', size: 'md', color: '#64748b', margin: 'sm', flex: 3 },
                                                { type: 'text', text: name, size: 'md', color: '#0f172a', weight: 'bold', align: 'end', flex: 6 }
                                            ]
                                        },
                                        {
                                            type: 'box',
                                            layout: 'horizontal',
                                            alignItems: 'center',
                                            contents: [
                                                { type: 'text', text: '🎯', size: 'md', flex: 0 },
                                                { type: 'text', text: ' เป้าหมาย', size: 'md', color: '#64748b', margin: 'sm', flex: 3 },
                                                { type: 'text', text: days > 0 ? `เลิกมา ${days} วัน` : 'ตั้งใจเลิกบุหรี่', size: 'md', color: '#0f172a', weight: 'bold', align: 'end', flex: 6 }
                                            ]
                                        },
                                        {
                                            type: 'box',
                                            layout: 'horizontal',
                                            alignItems: 'center',
                                            contents: [
                                                { type: 'text', text: '🌟', size: 'md', flex: 0 },
                                                { type: 'text', text: ' สิ่งสำคัญ', size: 'md', color: '#64748b', margin: 'sm', flex: 3 },
                                                { type: 'text', text: 'กำลังใจจากคุณ', size: 'md', color: '#e11d48', weight: 'bold', align: 'end', wrap: true, flex: 6 }
                                            ]
                                        }
                                    ]
                                }
                            ]
                        },
                        footer: {
                            type: 'box',
                            layout: 'vertical',
                            paddingAll: 'xl',
                            paddingTop: 'none',
                            contents: [
                                {
                                    type: 'button',
                                    style: 'primary',
                                    height: 'md',
                                    action: {
                                        type: 'uri',
                                        label: 'ยอมรับคำเชิญ ❤️',
                                        uri: liffUrl
                                    },
                                    color: '#7A2020'
                                }
                            ]
                        }
                    }
                };

                if (liff.isApiAvailable('shareTargetPicker')) {
                    const result = await liff.shareTargetPicker([flexMsg] as any, { isMultiple: false });
                    if (result) {
                        Toast.show({ content: 'ส่งคำเชิญเรียบร้อย! ❤️', icon: 'success' });
                    } else {
                        Toast.show({ content: 'ยกเลิกการส่ง', duration: 1500 });
                        await fetch(`${API_BASE_URL}/invite/${code}`, { method: 'DELETE' });
                    }
                    fetchInvites();
                    return;
                }
                if (navigator.share) {
                    await navigator.share({ title: 'ช่วยเป็นกำลังใจให้หน่อยนะ!', text: `${name} กำลังเลิกบุหรี่ ❤️`, url: appShareUrl });
                    fetchInvites();
                    return;
                }
                await navigator.clipboard.writeText(appShareUrl);
                Toast.show({ content: '📋 คัดลอกลิงก์แล้ว ส่งให้ครอบครัวได้เลย!', icon: 'success', duration: 3000 });
                fetchInvites();
            } catch (shareErr) {
                console.error('Share error:', shareErr);
                await fetch(`${API_BASE_URL}/invite/${code}`, { method: 'DELETE' });
                try {
                    await navigator.clipboard.writeText(appShareUrl);
                    Toast.show({ content: '📋 คัดลอกลิงก์แล้ว!', icon: 'success' });
                } catch {
                    // ignore
                }
                fetchInvites();
            }
        } catch (error) {
            console.error('Invite error:', error);
            Toast.show({ content: 'สร้างคำเชิญไม่สำเร็จ', icon: 'fail' });
        } finally {
            setLoadingInviteBtn(false);
        }
    };

    // Settings States
    const [showSmokingType, setShowSmokingType] = useState(false);
    const [showDuration, setShowDuration] = useState(false);
    const [showDailyAmount, setShowDailyAmount] = useState(false);
    const [showDailyLimit, setShowDailyLimit] = useState(false);
    const [showMonthlyCost, setShowMonthlyCost] = useState(false);
    const [showCostPerPuff, setShowCostPerPuff] = useState(false);
    const [showMode, setShowMode] = useState(false);

    // Temp values for edits
    const [tempDailyAmount, setTempDailyAmount] = useState(user?.dailyAmount || 10);
    const [tempDailyLimit, setTempDailyLimit] = useState(user?.dailyPuffLimit || 20);
    const [tempMonthlyCost, setTempMonthlyCost] = useState(user?.monthlyCost || 1500);
    const [tempCostPerPuff, setTempCostPerPuff] = useState(user?.costPerPuff || 5);
    const [tempSmokingType, setTempSmokingType] = useState(user?.smokingType || 'pod');
    const [tempSmokingDuration, setTempSmokingDuration] = useState(user?.smokingDuration || '<1y');
    const [tempMode, setTempMode] = useState(user?.mode || 'reduce');

    const saveSettings = async (settings: Record<string, unknown>) => {
        if (!user?.id) return;
        try {
            const res = await fetch(`${API_BASE_URL}/user/settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.id, ...settings }),
            });
            if (res.ok) {
                updateUserSettings(settings as Partial<typeof user>);
                Toast.show({ content: 'บันทึกแล้ว ✓', icon: 'success' });
            }
        } catch { } // fail silently or toast
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'white' }}>

            {/* 1. Header (Profile) */}
            <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid #f1f5f9' }} onClick={() => { setActiveTab('profile'); onClose(); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Avatar src={user?.pictureUrl || ''} style={{ '--size': '56px', '--border-radius': '50%' }} />
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{user?.displayName || 'User'}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12, color: '#64748b' }}>
                            <div style={{ fontSize: 14 }}>⭐️</div>
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>5.00</span> Rating <RightOutline fontSize={10} style={{ marginLeft: 2 }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div style={{ flex: 1, overflowY: 'auto' }}>

                {/* Navigation Group — hidden for now */}

                {/* 2.5 Invite Family */}
                <div style={{ padding: '12px 20px 4px' }}>
                    {/* Invite Button */}
                    <div
                        onClick={handleInvite}
                        style={{
                            background: 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 50%, #fce7f3 100%)',
                            borderRadius: 16, padding: '14px 16px', marginBottom: 16,
                            cursor: 'pointer', position: 'relative', overflow: 'hidden',
                            boxShadow: '0 2px 12px rgba(59, 130, 246, 0.1)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: 12, background: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 20, flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            }}>❤️</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>เชิญครอบครัวมาเชียร์</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>ส่งกำลังใจให้กัน</div>
                            </div>
                            <div style={{
                                width: 28, height: 28, borderRadius: 8,
                                background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: 'white', fontSize: 14, fontWeight: 700, flexShrink: 0,
                            }}>
                                {loadingInviteBtn ? <DotLoading color="white" /> : '+'}
                            </div>
                        </div>
                    </div>

                    {/* Invite List */}
                    {loadingInvites ? (
                        <div style={{ textAlign: 'center', padding: 12, color: '#94a3b8', fontSize: 13 }}>กำลังโหลด...</div>
                    ) : invites.length > 0 && (
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 8 }}>👪 ครอบครัวที่เชิญ</div>
                            {invites.map((inv, idx) => {
                                const daysAgo = Math.floor((Date.now() - new Date(inv.createdAt).getTime()) / (1000 * 60 * 60 * 24));
                                const dateLabel = daysAgo === 0 ? 'วันนี้' : daysAgo === 1 ? 'เมื่อวาน' : `${daysAgo} วันที่แล้ว`;
                                const isAccepted = !!inv.viewerName;
                                return (
                                    <div key={inv.code} style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '10px 0',
                                        borderBottom: idx < invites.length - 1 ? '1px solid #f1f5f9' : 'none',
                                    }}>
                                        <div style={{ position: 'relative' }}>
                                            {isAccepted && inv.viewerPicture ? (
                                                <img src={inv.viewerPicture} alt="" style={{
                                                    width: 36, height: 36, borderRadius: '50%', objectFit: 'cover',
                                                    border: '2px solid #86efac',
                                                }} />
                                            ) : (
                                                <div style={{
                                                    width: 36, height: 36, borderRadius: '50%',
                                                    background: isAccepted ? '#bbf7d0' : '#fef3c7',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 16,
                                                }}>{isAccepted ? '✅' : '⏳'}</div>
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
                                                {isAccepted ? inv.viewerName : (inv.label || 'ครอบครัว')}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                                {isAccepted ? (
                                                    <span style={{ color: '#16a34a', fontWeight: 600 }}>เชื่อมต่อแล้ว</span>
                                                ) : (
                                                    <span style={{ color: '#d97706', fontWeight: 600 }}>รอตอบรับ</span>
                                                )}
                                                {' · '}{dateLabel}
                                            </div>
                                        </div>
                                        <Button
                                            size='mini'
                                            onClick={() => handleDeleteInvite(inv.code)}
                                            style={{
                                                background: '#fef2f2', color: '#dc2626',
                                                border: '1px solid #fecaca', borderRadius: 8,
                                                fontSize: 11, fontWeight: 700, padding: '4px 10px',
                                            }}
                                        >ลบ</Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div style={{ height: 8, background: '#f8fafc' }} />

                {/* 3. Settings Group */}
                <div style={{ padding: '8px 0' }}>
                    <MenuItem icon={<Cloud size={24} strokeWidth={1.5} />} label="สิ่งที่สูบ" rightContent={user?.smokingType === 'pod' ? 'พอต' : user?.smokingType === 'cigarette' ? 'บุหรี่' : 'ทั้งสอง'} onClick={() => { setTempSmokingType(user?.smokingType || 'pod'); setShowSmokingType(true); }} />
                    <MenuItem icon={<Clock size={24} strokeWidth={1.5} />} label="สูบมานาน" rightContent={user?.smokingDuration === '<1y' ? 'น้อยกว่า 1 ปี' : user?.smokingDuration === '1-3y' ? '1-3 ปี' : user?.smokingDuration === '3-5y' ? '3-5 ปี' : 'มากกว่า 5 ปี'} onClick={() => { setTempSmokingDuration(user?.smokingDuration || '<1y'); setShowDuration(true); }} />
                    <MenuItem icon={<BarChart3 size={24} strokeWidth={1.5} />} label="จำนวนต่อวัน" rightContent={`${user?.dailyAmount} ${unit}`} onClick={() => { setTempDailyAmount(user?.dailyAmount || 10); setShowDailyAmount(true); }} />

                    <MenuItem icon={<Target size={24} strokeWidth={1.5} />} label="ลิมิตต่อวัน" rightContent={`${user?.dailyPuffLimit} ${unit}`} onClick={() => { setTempDailyLimit(user?.dailyPuffLimit || 20); setShowDailyLimit(true); }} />
                    <MenuItem icon={<Wallet size={24} strokeWidth={1.5} />} label="ค่าใช้จ่าย/เดือน" rightContent={`฿${(user?.monthlyCost || 0).toLocaleString()}`} onClick={() => { setTempMonthlyCost(user?.monthlyCost || 1500); setShowMonthlyCost(true); }} />
                    <MenuItem icon={<Rocket size={24} strokeWidth={1.5} />} label="โหมด" rightContent={user?.mode === 'quit' ? 'เลิกเลย' : 'ค่อยๆลด'} onClick={() => { setTempMode(user?.mode || 'reduce'); setShowMode(true); }} />
                </div>

                <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#cbd5e1' }}>
                    PordDee v1.0.0 (Build 2024.2)
                </div>

            </div>

            {/* Popup Modals */}
            <PopupModal visible={showSmokingType} onClose={() => setShowSmokingType(false)} title="สิ่งที่สูบ" subtitle="เลือกประเภทที่คุณใช้เป็นประจำ" icon={<Cloud size={32} />}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <OptionCard icon={<Wind />} label="พอต" selected={tempSmokingType === 'pod'} onClick={() => setTempSmokingType('pod')} />
                    <OptionCard icon={<Cigarette />} label="บุหรี่มวน" selected={tempSmokingType === 'cigarette'} onClick={() => setTempSmokingType('cigarette')} color="#f97316" />
                </div>
                <ModalActions onCancel={() => setShowSmokingType(false)} onConfirm={async () => { await saveSettings({ smokingType: tempSmokingType }); setShowSmokingType(false); }} />
            </PopupModal>

            <PopupModal visible={showDuration} onClose={() => setShowDuration(false)} title="สูบมานานแค่ไหน" subtitle="ระยะเวลาที่คุณเริ่มสูบ" icon={<Clock size={32} />}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <OptionCard icon={<Leaf />} label="น้อยกว่า 1 ปี" selected={tempSmokingDuration === '<1y'} onClick={() => setTempSmokingDuration('<1y')} color="#22c55e" />
                    <OptionCard icon={<Clock4 />} label="1 - 3 ปี" selected={tempSmokingDuration === '1-3y'} onClick={() => setTempSmokingDuration('1-3y')} color="#eab308" />
                    <OptionCard icon={<Clock8 />} label="3 - 5 ปี" selected={tempSmokingDuration === '3-5y'} onClick={() => setTempSmokingDuration('3-5y')} color="#f97316" />
                    <OptionCard icon={<Clock12 />} label="มากกว่า 5 ปี" selected={tempSmokingDuration === '>5y'} onClick={() => setTempSmokingDuration('>5y')} color="#ef4444" />
                </div>
                <ModalActions onCancel={() => setShowDuration(false)} onConfirm={async () => { await saveSettings({ smokingDuration: tempSmokingDuration }); setShowDuration(false); }} />
            </PopupModal>

            <PopupModal visible={showDailyAmount} onClose={() => setShowDailyAmount(false)} title={`ปกติสูบวันละกี่${unit}?`} subtitle="ประมาณจำนวนที่คุณสูบต่อวัน" icon={<BarChart3 size={32} />}>
                <NumberInputSheet value={tempDailyAmount} onChange={setTempDailyAmount} onConfirm={async () => { await saveSettings({ dailyAmount: tempDailyAmount }); setShowDailyAmount(false); }} onCancel={() => setShowDailyAmount(false)} unit={unit} max={100} presets={[5, 10, 20]} />
            </PopupModal>

            <PopupModal visible={showDailyLimit} onClose={() => setShowDailyLimit(false)} title="ตั้งเป้าลิมิตต่อวัน" subtitle="กำหนดจำนวนสูงสุดที่จะสูบในแต่ละวัน" icon={<Target size={32} />}>
                <NumberInputSheet value={tempDailyLimit} onChange={setTempDailyLimit} onConfirm={async () => { await saveSettings({ dailyPuffLimit: tempDailyLimit }); setShowDailyLimit(false); }} onCancel={() => setShowDailyLimit(false)} unit={unit} max={100} presets={[10, 15, 20]} />
            </PopupModal>

            <PopupModal visible={showMonthlyCost} onClose={() => setShowMonthlyCost(false)} title="ค่าใช้จ่ายต่อเดือน" subtitle="ปรับราคาเพื่อให้คำนวณเงินออม" icon={<Wallet size={32} />}>
                <CostInputSheet value={tempMonthlyCost} onChange={setTempMonthlyCost} onConfirm={async () => { await saveSettings({ monthlyCost: tempMonthlyCost }); setShowMonthlyCost(false); }} onCancel={() => setShowMonthlyCost(false)} presets={[500, 1000, 2000, 3000]} />
            </PopupModal>

            <PopupModal visible={showCostPerPuff} onClose={() => setShowCostPerPuff(false)} title={`ราคาต่อ${unit} (โดยประมาณ)`} subtitle="ปรับราคาต่อคำเพื่อความแม่นยำ" icon={<CircleDollarSign size={32} />}>
                <CostInputSheet value={tempCostPerPuff} onChange={setTempCostPerPuff} onConfirm={async () => { await saveSettings({ costPerPuff: tempCostPerPuff }); setShowCostPerPuff(false); }} onCancel={() => setShowCostPerPuff(false)} presets={[1, 2, 5, 10]} />
            </PopupModal>

            <PopupModal visible={showMode} onClose={() => setShowMode(false)} title="โหมดการใช้งาน" subtitle="เลือกวิธีการเลิกบุหรี่ของคุณ" icon={<Rocket size={32} />}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <OptionCard icon={<TrendingDown />} label="ค่อยๆ ลดปริมาณ" selected={tempMode === 'reduce'} onClick={() => setTempMode('reduce')} color={theme.primary} />
                    <OptionCard icon={<Ban />} label="หักดิบ / เลิกถาวร" selected={tempMode === 'quit'} onClick={() => setTempMode('quit')} color="#ef4444" />
                </div>
                <ModalActions onCancel={() => setShowMode(false)} onConfirm={async () => { await saveSettings(tempMode === 'quit' ? { mode: 'quit', quitDate: new Date().toISOString() } : { mode: 'reduce' }); setShowMode(false); }} />
            </PopupModal>
        </div>
    );
};
